import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, LineElement, CategoryScale,
  LinearScale, PointElement, Tooltip, Legend, Filler,
} from "chart.js";
import api from "../api/axios";
import AdminLayout from "./AdminLayout";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler);

/* ── icons ── */
const TrendUpIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 12l4-4 3 2 4-5 3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const CalendarIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="2" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M1 6h14M5 1v2M11 1v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const SpinIcon      = () => <div style={{ width:24, height:24, border:"2px solid #2a2d38", borderTopColor:"#4ade80", borderRadius:"50%", animation:"spin 0.6s linear infinite" }} />;

const PERIODS = [
  { label:"7j",  days:7  },
  { label:"30j", days:30 },
  { label:"90j", days:90 },
];

const AdminRevenue = () => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState(30);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get("/admin/revenue-chart");
      setData(res.data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  /* slice to selected period */
  const sliced = data.slice(-period);

  /* KPIs */
  const total   = sliced.reduce((sum, d) => sum + (d.amount || 0), 0);
  const prev    = data.slice(-period * 2, -period);
  const prevTot = prev.reduce((sum, d) => sum + (d.amount || 0), 0);
  const growth  = prevTot ? (((total - prevTot) / prevTot) * 100).toFixed(1) : null;
  const avg     = sliced.length ? Math.round(total / sliced.length) : 0;
  const peak    = sliced.length ? Math.max(...sliced.map(d => d.amount || 0)) : 0;

  const chartData = {
    labels: sliced.map(d => d.date),
    datasets: [{
      label: "Revenus (MGA)",
      data: sliced.map(d => d.amount),
      borderColor: "#4ade80",
      backgroundColor: "rgba(74,222,128,0.06)",
      borderWidth: 1.5,
      tension: 0.4,
      fill: true,
      pointRadius: sliced.length <= 14 ? 4 : 2,
      pointBackgroundColor: "#4ade80",
      pointBorderColor: "#16181f",
      pointBorderWidth: 2,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode:"index", intersect:false },
    plugins: {
      legend: { display:false },
      tooltip: {
        backgroundColor:"#1e2029",
        titleColor:"#e8e9ed",
        bodyColor:"#8b8fa8",
        borderColor:"#2a2d38",
        borderWidth:1,
        callbacks: {
          label: ctx => ` ${Number(ctx.parsed.y).toLocaleString()} MGA`,
        },
      },
    },
    scales: {
      x: {
        grid:  { color:"#1a1c25" },
        ticks: { color:"#8b8fa8", font:{ size:11, family:"'DM Mono',monospace" }, maxTicksLimit:8 },
      },
      y: {
        grid:  { color:"#1a1c25" },
        ticks: {
          color:"#8b8fa8", font:{ size:11, family:"'DM Mono',monospace" },
          callback: v => `${(v/1000).toFixed(0)}k`,
        },
      },
    },
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#0f1117", flexDirection:"column", gap:12 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <SpinIcon />
      <p style={{ color:"#8b8fa8", fontSize:13 }}>Chargement des revenus…</p>
    </div>
  );

  return (
    <AdminLayout title="Revenus" subtitle="Analyse financière de la plateforme">
      <style>{css}</style>

      {/* KPI CARDS */}
      <div style={s.kpiGrid} className="kpi-grid">
        {[
          {
            label:"Revenus totaux",
            value:`${total.toLocaleString()} MGA`,
            sub: growth !== null ? `${growth > 0 ? "+" : ""}${growth}% vs période précédente` : "Première période",
            icon:<TrendUpIcon />,
            color:"#4ade80",
          },
          {
            label:"Revenu moyen / jour",
            value:`${avg.toLocaleString()} MGA`,
            sub:"Sur la période sélectionnée",
            icon:<CalendarIcon />,
            color:"#60a5fa",
          },
          {
            label:"Pic journalier",
            value:`${peak.toLocaleString()} MGA`,
            sub:"Meilleure journée",
            icon:<TrendUpIcon />,
            color:"#fbbf24",
          },
          {
            label:"Jours analysés",
            value:sliced.length,
            sub:`Période de ${period} jours`,
            icon:<CalendarIcon />,
            color:"#a78bfa",
          },
        ].map(({ label, value, sub, icon, color }) => (
          <div key={label} style={s.kpiCard} className="kpi-card">
            <div style={{ ...s.kpiIcon, background:`${color}15`, color }}>{icon}</div>
            <div style={s.kpiValue}>{value}</div>
            <div style={s.kpiLabel}>{label}</div>
            <div style={s.kpiSub}>{sub}</div>
          </div>
        ))}
      </div>

      {/* CHART CARD */}
      <div style={s.chartCard}>
        <div style={s.chartHeader}>
          <div>
            <h2 style={s.chartTitle}>Évolution des revenus</h2>
            <div style={s.chartSub}>MGA par jour</div>
          </div>
          <div style={s.periodTabs}>
            {PERIODS.map(p => (
              <button
                key={p.days}
                style={{ ...s.periodTab, ...(period===p.days ? s.periodTabActive : {}) }}
                onClick={() => setPeriod(p.days)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height:280 }}>
          {sliced.length > 0
            ? <Line data={chartData} options={chartOptions} />
            : <div style={s.noData}>Aucune donnée disponible pour cette période</div>
          }
        </div>
      </div>

      {/* TABLE */}
      <div style={s.tableCard}>
        <div style={s.tableHeader}>
          <h2 style={s.chartTitle}>Détail par jour</h2>
          <span style={s.tableBadge}>{sliced.length} entrées</span>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={s.table}>
            <thead>
              <tr>
                {["Date","Revenus","Variation"].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...sliced].reverse().map((d, i, arr) => {
                const prev  = arr[i+1]?.amount || 0;
                const diff  = prev ? (((d.amount - prev) / prev) * 100).toFixed(1) : null;
                const up    = diff !== null && parseFloat(diff) >= 0;
                return (
                  <tr key={d.date} className="drow" style={s.tr}>
                    <td style={{ ...s.td, fontFamily:"'DM Mono',monospace", fontSize:12 }}>{d.date}</td>
                    <td style={{ ...s.td, fontFamily:"'DM Mono',monospace", color:"#4ade80", fontWeight:600 }}>
                      {Number(d.amount).toLocaleString()} MGA
                    </td>
                    <td style={s.td}>
                      {diff !== null ? (
                        <span style={{ fontSize:11, fontWeight:600, color: up ? "#4ade80" : "#f87171" }}>
                          {up ? "▲" : "▼"} {Math.abs(diff)}%
                        </span>
                      ) : <span style={{ color:"#4a4d5a", fontSize:11 }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRevenue;

const s = {
  kpiGrid:     { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:18 },
  kpiCard:     { background:"#16181f", border:"0.5px solid #2a2d38", borderRadius:12, padding:"18px 20px" },
  kpiIcon:     { width:32, height:32, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 },
  kpiValue:    { fontSize:20, fontWeight:700, color:"#e8e9ed", letterSpacing:-0.5, marginBottom:2, fontFamily:"'DM Mono',monospace" },
  kpiLabel:    { fontSize:12, color:"#8b8fa8", marginBottom:2 },
  kpiSub:      { fontSize:11, color:"#4a4d5a" },

  chartCard:   { background:"#16181f", border:"0.5px solid #2a2d38", borderRadius:12, padding:"20px 24px", marginBottom:18 },
  chartHeader: { display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 },
  chartTitle:  { fontSize:14, fontWeight:600, color:"#e8e9ed", margin:0 },
  chartSub:    { fontSize:11, color:"#8b8fa8", marginTop:3 },
  periodTabs:  { display:"flex", gap:4 },
  periodTab:   { fontSize:11, padding:"5px 12px", borderRadius:8, border:"0.5px solid #2a2d38", background:"none", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", color:"#8b8fa8" },
  periodTabActive:{ background:"#1e2029", color:"#e8e9ed", borderColor:"#3a3d4a" },
  noData:      { display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:13, color:"#8b8fa8" },

  tableCard:   { background:"#16181f", border:"0.5px solid #2a2d38", borderRadius:12, overflow:"hidden" },
  tableHeader: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:"0.5px solid #2a2d38" },
  tableBadge:  { fontSize:11, padding:"3px 9px", borderRadius:20, background:"rgba(74,222,128,0.1)", color:"#4ade80", fontWeight:600 },
  table:       { width:"100%", borderCollapse:"collapse", fontSize:13 },
  th:          { padding:"10px 16px", color:"#8b8fa8", fontSize:11, fontWeight:500, textTransform:"uppercase", letterSpacing:"0.5px", borderBottom:"0.5px solid #2a2d38", textAlign:"left", background:"#13151b" },
  td:          { padding:"11px 16px", borderBottom:"0.5px solid #1e2029", color:"#c8cad8", verticalAlign:"middle" },
  tr:          {},
};

const css = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .drow:hover td { background: #1a1c25 !important; }
  .kpi-card:hover { border-color: #3a3d4a !important; }
  @media(max-width:900px) { .kpi-grid { grid-template-columns: 1fr 1fr !important; } }
  @media(max-width:480px) { .kpi-grid { grid-template-columns: 1fr !important; } }
`;